
import { set } from 'lodash';
import { RefObject, useEffect, useRef, useState } from 'react';

import { InteractionData } from './interaction-data.type';
import { useInteraction } from './use-interaction'; // Assumed external hook

type HoverActivatedMouse = [
	InteractionData & {
		//   isDragging: boolean;
		isActive: boolean;
		isHovering: boolean;
		setActive: () => void;
	},
	{
		setIsEnabled: (enabled: boolean) => void;
	}
];

type UseHoverActivatedMouseOptions = {
	enabled?: boolean;
	// cursor [default cursor, hover cursor]
	cursor?: [string, string];
};

export const useHoverActivatedMouse = (ref: RefObject<SVGElement>, options: UseHoverActivatedMouseOptions = {}): HoverActivatedMouse => {
	const [isHovering, setIsHovering] = useState(false);
	const [isActive, setIsActive] = useState(false);
	const [isEnabled, setIsEnabled] = useState<boolean>(options.enabled ?? true);
	// const [cursor, setCursor] = useState<null | string>(null);

	// Sync isEnabled with incoming options.enabled prop
	useEffect(() => {
		setIsEnabled(options.enabled ?? true);
	}, [options.enabled]);

	// Use ref to store drag state without triggering rerenders unnecessarily
	const isDraggingRef = useRef(false);

	const [mouse, { setActive }] = useInteraction(ref, { active: false });

	// useEffect(() => {
	// 	setActive(isActive);
	// }, [isActive]);

	// Sync ref and state for dragging
	useEffect(() => {
		if (isEnabled) {
			isDraggingRef.current = mouse.isDragging;
			if (mouse.isDragging) {
				setIsActive(true); // Stay active while dragging
			} else if (!isHovering) {
				setActive(false);
				setIsActive(false); // Stop tracking if not hovering
			}
		}
	}, [mouse.isDragging, isHovering, isEnabled]);

	// Hover detection
	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const handleEnter = () => {
			setActive(true);
			setIsHovering(true);
			if (options.cursor) ref.current.setAttribute('cursor', options.cursor[1]);
		};

		const handleLeave = () => {
			setIsHovering(false);
			if (!isDraggingRef.current) {
				setActive(false);
				if (options.cursor) ref.current.setAttribute('cursor', options.cursor[0]);
			}
		};

		if (isEnabled) {
			element.addEventListener('mouseenter', handleEnter);
			element.addEventListener('mouseleave', handleLeave);
		}
		// eslint-disable-next-line consistent-return
		return () => {
			element.removeEventListener('mouseenter', handleEnter);
			element.removeEventListener('mouseleave', handleLeave);
		};
		// ref.current (not just ref) so a conditionally-unmounted/remounted target (e.g. a hidden-then-shown marker) gets its listeners re-attached to the new DOM node -- see use-draggable-svg's same fix.
	}, [ref?.current, isEnabled]);

	// Only allow starting drag when hovering
	const guardedActivation = () => {
		if (isHovering) {
			setIsActive(true);
			// startDrag();
		}
	};

	return [
		{
			...mouse,
			isActive,
			isHovering,
			setActive: guardedActivation
		},
		{
			setIsEnabled
		}
	];
};
